import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import apiClient from '@/api/client'

export interface AuthUser {
  id: number
  displayName: string | null
  avatarUrl: string | null
}

const ACCESS_TOKEN_KEY = 'accessToken'
const REFRESH_TOKEN_KEY = 'refreshToken'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthUser | null>(null)
  const accessToken = ref<string | null>(localStorage.getItem(ACCESS_TOKEN_KEY))

  const isAuthenticated = computed(() => !!accessToken.value)

  function setTokens(at: string, rt: string): void {
    accessToken.value = at
    localStorage.setItem(ACCESS_TOKEN_KEY, at)
    localStorage.setItem(REFRESH_TOKEN_KEY, rt)
  }

  function clearTokens(): void {
    accessToken.value = null
    user.value = null
    localStorage.removeItem(ACCESS_TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
  }

  function loginWithGoogle(): void {
    const apiBase = import.meta.env.VITE_API_URL ?? '/api/v1'
    window.location.href = `${apiBase}/auth/google`
  }

  async function loginWithEmail(email: string, password: string): Promise<void> {
    const { data } = await apiClient.post<{
      accessToken: string
      refreshToken: string
      user: AuthUser
    }>('/auth/login', { email, password })
    setTokens(data.accessToken, data.refreshToken)
    user.value = data.user
  }

  async function register(email: string, password: string, displayName?: string): Promise<void> {
    const { data } = await apiClient.post<{
      accessToken: string
      refreshToken: string
      user: AuthUser
    }>('/auth/register', { email, password, displayName })
    setTokens(data.accessToken, data.refreshToken)
    user.value = data.user
  }

  async function logout(): Promise<void> {
    const rt = localStorage.getItem(REFRESH_TOKEN_KEY)
    try {
      if (rt) await apiClient.post('/auth/logout', { refreshToken: rt })
    } catch {
      // ignore errors — clear local state regardless
    }
    clearTokens()
  }

  async function refreshAccessToken(): Promise<string> {
    const rt = localStorage.getItem(REFRESH_TOKEN_KEY)
    if (!rt) throw new Error('No refresh token available')
    const { data } = await apiClient.post<{ accessToken: string; refreshToken: string }>(
      '/auth/refresh',
      { refreshToken: rt }
    )
    setTokens(data.accessToken, data.refreshToken)
    return data.accessToken
  }

  async function fetchMe(): Promise<void> {
    const { data } = await apiClient.get<{ user: AuthUser }>('/auth/me')
    user.value = data.user
  }

  return {
    user,
    accessToken,
    isAuthenticated,
    loginWithGoogle,
    loginWithEmail,
    register,
    logout,
    refreshAccessToken,
    fetchMe,
    setTokens,
    clearTokens,
  }
})
